/**
 * Promotion tracking: view_promotion (first time a promo scrolls into view) and
 * select_promotion (click). Any element tagged `data-analytics-promo` with
 * `data-promotion-name`, `data-promotion-id`, `data-creative-name`,
 * `data-creative-slot` is tracked. Each promo reports view_promotion once.
 */
import { track } from '@theme/analytics';

/** @type {WeakSet<Element>} promos already reported as viewed */
const seen = new WeakSet();

/**
 * @param {Element} el
 * @returns {Record<string, string>}
 */
function promoPayload(el) {
  return {
    promotion_name: el.getAttribute('data-promotion-name') || '',
    promotion_id: el.getAttribute('data-promotion-id') || '',
    creative_name: el.getAttribute('data-creative-name') || '',
    creative_slot: el.getAttribute('data-creative-slot') || '',
  };
}

/** @param {Element} el */
function fireView(el) {
  if (seen.has(el)) return;
  seen.add(el);
  track('view_promotion', promoPayload(el));
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = /** @type {Element} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;
  const el = target.closest('[data-analytics-promo]');
  if (el) track('select_promotion', promoPayload(el));
}

function init() {
  const promos = document.querySelectorAll('[data-analytics-promo]');
  if (promos.length) {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              fireView(entry.target);
              obs.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.3 }
      );
      promos.forEach((el) => observer.observe(el));
    } else {
      promos.forEach(fireView);
    }
  }

  document.addEventListener('click', onClick, { capture: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
